import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { type Board, type ListResponse, type SubnavBoardSort } from '@air/types';

import { getPrivateBoardsListKey } from '~/components/v2/hooks/queries/usePrivateBoardsList';
import { useSubBoardsParams } from '~/components/v2/hooks/useSubBoardsParams';
import { getRearrangedBoards } from '~/utils/mutateUtils/mutators';
import { addBoards, removeBoards } from '~/utils/mutateUtils/SideNavBoards';

export const useSubBoardsCacheUpdate = () => {
  const queryClient = useQueryClient();
  const { getSubBoardsParams } = useSubBoardsParams();

  const updateSubBoardsCache = useCallback(
    ({
      parentBoard,
      sort,
      rearrangedBoards,
    }: {
      parentBoard: Pick<Board, 'id'>;
      sort: SubnavBoardSort | undefined;
      rearrangedBoards: Board[];
    }) => {
      queryClient.setQueriesData<InfiniteData<ListResponse<Board>> | undefined>(
        {
          queryKey: getPrivateBoardsListKey(getSubBoardsParams({ parentBoard, sort })),
        },
        getRearrangedBoards(rearrangedBoards),
      );
    },
    [getSubBoardsParams, queryClient],
  );

  const addBoardsToSubBoardsCache = useCallback(
    ({
      parentBoard,
      sort,
      boards,
    }: {
      parentBoard: Pick<Board, 'id'>;
      sort: SubnavBoardSort | undefined;
      boards: Board[];
    }) => {
      queryClient.setQueriesData<InfiniteData<ListResponse<Board>> | undefined>(
        {
          queryKey: getPrivateBoardsListKey(getSubBoardsParams({ parentBoard, sort })),
        },
        addBoards(boards),
      );
    },
    [getSubBoardsParams, queryClient],
  );

  const removeBoardsFromSubBoardsCache = useCallback(
    ({
      parentBoard,
      sort,
      boardIds,
    }: {
      parentBoard: Pick<Board, 'id'>;
      sort: SubnavBoardSort | undefined;
      boardIds: string[];
    }) => {
      queryClient.setQueriesData<InfiniteData<ListResponse<Board>> | undefined>(
        {
          queryKey: getPrivateBoardsListKey(getSubBoardsParams({ parentBoard, sort })),
        },
        removeBoards(boardIds),
      );
    },
    [getSubBoardsParams, queryClient],
  );

  return {
    updateSubBoardsCache,
    addBoardsToSubBoardsCache,
    removeBoardsFromSubBoardsCache,
  };
};
